// A7 — thông báo: logic thuần (không đụng DOM) để Main/EmbedApp dùng và test E2E import trực tiếp
// (Node chạy được file .ts nhờ type stripping → chỉ dùng cú pháp TS xóa được, chỉ `import type`).
import type { ChatToPortal, WithoutEnvelope } from '@swchat/protocol';

export const APP_TITLE = 'SW Chat';
/** W2.2: nhãn tin trả lời trong luồng (khớp `THREAD_PREVIEW_PREFIX` của SDK — file này chỉ `import type`). */
const THREAD_PREVIEW_PREFIX = 'Trả lời trong luồng: ';

/** Tiêu đề tab: `(n) SW Chat` khi còn tin chưa đọc. */
export function unreadTitle(total: number): string {
  return total > 0 ? `(${total}) ${APP_TITLE}` : APP_TITLE;
}

/** Quyền Notification của trình duyệt; `unsupported` khi không có API. */
export type NotifyPermission = 'default' | 'granted' | 'denied' | 'unsupported';

/**
 * Hiện nút "Bật thông báo"? Chỉ khi chưa hỏi và không nằm trong iframe (iframe khác origin không xin được quyền —
 * portal tự báo qua `unread`).
 */
export function canAskPermission(permission: NotifyPermission, topLevel: boolean): boolean {
  return permission === 'default' && topLevel;
}

export interface IncomingMessage {
  sender_user_id: string;
  type: string;
  text?: string;
  /** Tin file (C1): tên file đã giải mã trên máy. */
  file?: { name: string };
  /** W2.2: tin trả lời trong luồng (vẫn báo như tin thường, kèm nhãn luồng). */
  thread_root?: string;
}

export interface NotificationSpec {
  title: string;
  body: string;
  /** Gộp thông báo theo hội thoại (tin sau thay tin trước). */
  tag: string;
}

/**
 * Nội dung thông báo cho tin mới (đã giải mã trên máy — không qua server), hoặc `undefined` khi không cần báo:
 * tab đang hiển thị, chưa được cấp quyền, hoặc không có tin.
 */
export function incomingNotification(input: {
  visibility: string;
  permission: NotifyPermission;
  conversationId: string;
  conversationTitle?: string;
  senderName?: string;
  messages: readonly IncomingMessage[];
}): NotificationSpec | undefined {
  if (input.visibility === 'visible' || input.permission !== 'granted') return undefined;
  const last = input.messages.at(-1);
  if (!last) return undefined;
  const title = input.conversationTitle ?? APP_TITLE;
  const sender = input.senderName ?? 'Tin nhắn mới';
  const content =
    (last.thread_root ? THREAD_PREVIEW_PREFIX : '') + (last.type === 'file' ? `📎 ${last.file?.name ?? 'Tệp đính kèm'}` : last.type === 'poll' ? `📊 ${last.text ?? 'Bình chọn'}` : last.type === 'location' ? `📍 ${last.text || 'Vị trí'}` : (last.text ?? ''));
  // Hội thoại 1-1: tiêu đề đã là tên người gửi → không lặp lại.
  const body = `${title === sender ? '' : `${sender}: `}${content}`.slice(0, 200);
  return { title, body, tag: input.conversationId };
}

/** Thêm envelope `{source, v}` cho message gửi portal (docs/07-portal-integration.md §2.1). */
export function toPortal(msg: WithoutEnvelope<ChatToPortal>): ChatToPortal {
  return { source: 'swchat', v: 1, ...msg } as ChatToPortal;
}

/** Bộ báo tổng chưa đọc cho portal: chỉ gửi `{type:'unread'}` khi số thay đổi. */
export function unreadReporter(send: (msg: WithoutEnvelope<ChatToPortal>) => void): (count: number) => void {
  let last: number | undefined;
  return (count) => {
    if (last === count) return;
    last = count;
    send({ type: 'unread', count });
  };
}
